import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

const baseStyles = {
  cursor: 'pointer',
  borderRadius: theme.radii.input,
  fontWeight: theme.fontWeights.medium,
  transition: theme.transitions.default,
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

const VARIANTS = {
  primary: {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.primary,
    color: theme.colors.white,
    border: 'none',
    '&:hover:not(:disabled)': {
      background: theme.colors.primaryHover,
    },
  },
  'primary-md': {
    ...baseStyles,
    padding: theme.spacing.mediumButtonPadding,
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    background: theme.colors.primary,
    color: theme.colors.white,
    border: 'none',
    whiteSpace: 'nowrap',
    '&:hover:not(:disabled)': {
      background: theme.colors.primaryHover,
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      background: theme.colors.disabled,
    },
  },
  'primary-lg': {
    ...baseStyles,
    padding: theme.spacing.buttonPadding,
    ...theme.typography.button,
    background: theme.colors.primary,
    color: theme.colors.white,
    border: 'none',
    borderRadius: theme.radii.inner,
    minWidth: '250px',
    '&:hover:not(:disabled)': {
      background: theme.colors.primaryHover,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows.buttonHover,
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      background: theme.colors.disabled,
    },
  },
  'warning-outline': {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.bgCard,
    color: theme.colors.warningText,
    border: `1px solid ${theme.colors.warningText}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.warningBg,
      borderColor: theme.colors.warningHover,
      color: theme.colors.warningHover,
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      background: theme.colors.bgMuted,
      color: theme.colors.border,
      borderColor: theme.colors.border,
    },
  },
  warning: {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.warning,
    color: theme.colors.white,
    border: 'none',
    '&:hover:not(:disabled)': {
      background: theme.colors.warningHover,
    },
  },
  consolidate: {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.semibold,
    background: theme.colors.consolidate,
    color: theme.colors.white,
    border: 'none',
    whiteSpace: 'nowrap',
    '&:hover:not(:disabled)': {
      background: theme.colors.consolidateHover,
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      background: theme.colors.disabled,
    },
  },
  secondary: {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.bgCard,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.bgMuted,
    },
  },
  // A secondary button at the primary-md size — for the one control that is
  // a page's only way out, such as the route map's Back.
  'secondary-md': {
    ...baseStyles,
    padding: theme.spacing.mediumButtonPadding,
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    background: theme.colors.bgCard,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    whiteSpace: 'nowrap',
    '&:hover:not(:disabled)': {
      background: theme.colors.bgMuted,
    },
  },
  // Ghost button for dark surfaces (e.g. the consolidation shelf).
  'secondary-inverse': {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: 'transparent',
    color: theme.colors.white,
    border: `1px solid ${theme.colors.whiteOverlay}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.whiteOverlay,
    },
  },
  'link-primary': {
    ...baseStyles,
    padding: 0,
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    fontSize: theme.typography.label.fontSize,
    '&:hover:not(:disabled)': {
      color: theme.colors.primaryHover,
      textDecoration: 'underline',
    },
  },
  'link-danger': {
    ...baseStyles,
    padding: 0,
    background: 'none',
    border: 'none',
    color: theme.colors.danger,
    fontSize: theme.typography.label.fontSize,
    '&:hover:not(:disabled)': {
      color: theme.colors.dangerHover,
      textDecoration: 'underline',
    },
  },
  'danger-outline': {
    ...baseStyles,
    padding: theme.spacing.removeBtnPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.bgCard,
    color: theme.colors.danger,
    border: `1px solid ${theme.colors.dangerBorder}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.dangerBg,
    },
  },
  'info-outline': {
    ...baseStyles,
    padding: theme.spacing.removeBtnPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.bgCard,
    color: theme.colors.infoBlue,
    border: `1px solid ${theme.colors.infoBorder}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.infoBg,
    },
  },
  link: {
    ...baseStyles,
    padding: theme.spacing.iconPadding,
    fontSize: theme.sizes.smallFontSize,
    background: 'none',
    color: theme.colors.textBody,
    border: 'none',
    textDecoration: 'underline',
    '&:hover:not(:disabled)': {
      color: theme.colors.infoBlue,
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      textDecoration: 'none',
    },
  },
};

// A clickable legend entry: a swatch plus a label that reads as plain legend
// text until toggled on (`pressed`), when it fills to show it is filtering.
VARIANTS['legend-pill'] = {
  ...baseStyles,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing.gapSm,
  padding: theme.spacing.smallBadgePadding,
  fontSize: theme.sizes.captionFontSize,
  fontWeight: theme.fontWeights.normal,
  background: 'none',
  color: 'inherit',
  border: '1px solid transparent',
  borderRadius: theme.radii.pill,
  '&:hover:not(:disabled)': {
    background: theme.colors.bgMuted,
  },
  '&[aria-pressed="true"]': {
    background: theme.colors.bgMuted,
    borderColor: theme.colors.border,
    color: theme.colors.textDark,
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
};

const styledButtons = {};
Object.keys(VARIANTS).forEach((variant) => {
  styledButtons[variant] = styledComponent('button', VARIANTS[variant]);
});

function Button({
  variant,
  onClick,
  disabled,
  isLoading,
  loadingText,
  children,
  dataTestId,
  title,
  pressed,
}) {
  const StyledButton = styledButtons[variant];
  return (
    <StyledButton
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      aria-pressed={pressed}
      data-testid={dataTestId}
      title={title}
    >
      {isLoading ? loadingText : children}
    </StyledButton>
  );
}

Button.propTypes = {
  variant: PropTypes.oneOf(Object.keys(VARIANTS)).isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadingText: PropTypes.node,
  children: PropTypes.node.isRequired,
  dataTestId: PropTypes.string,
  title: PropTypes.string,
  // Toggle buttons only: on/off state, exposed as aria-pressed. Leave unset
  // on ordinary buttons so they carry no toggle semantics.
  pressed: PropTypes.bool,
};

Button.defaultProps = {
  onClick: undefined,
  disabled: false,
  isLoading: false,
  loadingText: 'Loading…',
  dataTestId: undefined,
  title: undefined,
  pressed: undefined,
};

export default Button;
